import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { Apps } from '../../shared/enums/enum';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { SharedService } from '../../shared/services/shared.service';
import { UserService } from '../../shared/services/user.service';
import { SharedUserApiService } from '../../shared/services/shared-user-api.service';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private permissions = new Set<string>();
  private roles = new Set<string>();
  private isLoaded = false;
  private loadedAppId: number | null = null;
  private loadInFlight: Promise<void> | null = null;

  constructor(
    private readonly ngxPermissions: NgxPermissionsService,
    private readonly sharedService: SharedService,
    private readonly userDataService: UserService,
    private readonly sharedUserApiService: SharedUserApiService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  ensurePermissionsLoaded(appId: number = Apps.IMS): Promise<void> {
    if (this.isLoaded && this.loadedAppId === appId) {
      return Promise.resolve();
    }
    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = new Promise<void>((resolve) => {
      const user = this.userDataService.getUserFromLocalStorage();
      if (!user || !user.userId) {
        this.flush();
        resolve();
        return;
      }

      const cachedPermissions = this.sharedService.getCachedPermissions(user.userId, appId);
      if (cachedPermissions && cachedPermissions.length > 0) {
        this.permissions = new Set(cachedPermissions);
        this.ngxPermissions.flushPermissions();
        this.ngxPermissions.loadPermissions(cachedPermissions);
        this.isLoaded = true;
        this.loadedAppId = appId;
        resolve();
        return;
      }

      this.sharedUserApiService.getPermissionsByUserId(user.userId).subscribe({
        next: (roles) => {
          try {
            const appRoles = (roles || []).filter((r: any) => r?.appId === appId);

            const nextPermissions = new Set<string>();
            appRoles.forEach((role: any) => {
              (role?.permissions || []).forEach((p: any) => {
                if (p?.name) {
                  nextPermissions.add(String(p.name));
                }
              });
            });

            this.roles = new Set(appRoles.map((r) => String(r.name)));
            this.permissions = nextPermissions;
            this.ngxPermissions.flushPermissions();
            this.ngxPermissions.loadPermissions(Array.from(this.permissions));

            this.isLoaded = true;
            this.loadedAppId = appId;

            this.sharedService.setCachedPermissions(
              user.userId,
              appId,
              Array.from(this.permissions),
            );
          } finally {
            resolve();
          }
        },
        error: () => {
          this.flush();
          resolve();
        },
      });
    })
      .catch((error) => {
        this.errorHandlerService.handleApiError(error);
      })
      .finally(() => {
        this.loadInFlight = null;
      });

    return this.loadInFlight;
  }

  hasPermission(permissionName: string): boolean {
    return this.permissions.has(permissionName);
  }

  getAllPermissions(): string[] {
    return Array.from(this.permissions);
  }

  getAllRoles(): string[] {
    return Array.from(this.roles);
  }

  flush(): void {
    this.permissions.clear();
    this.ngxPermissions.flushPermissions();
    this.isLoaded = false;
    this.loadedAppId = null;

    const user = this.userDataService.getUserFromLocalStorage();
    if (user?.userId) {
      this.sharedService.clearCachedPermissions(user.userId);
    }
  }
}
